import React from 'react';
import {
  Box,
  Collapse,
  Switch,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
};

export interface CollapsiblePanelProps {
  title: string;
  subtitle?: string;
  required?: boolean;
  enabled: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  disabled?: boolean;
  invalid?: boolean;
  children: React.ReactNode;
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  title,
  subtitle,
  required = false,
  enabled,
  onEnabledChange,
  disabled = false,
  invalid = false,
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const inkSecondary = isDark ? '#b0bec5' : '#546e7a';
  const expanded = required || enabled;

  const headerBg = expanded
    ? isDark
      ? alpha(NAUTILUS.sand, 0.08)
      : alpha(NAUTILUS.deepOcean, 0.04)
    : 'transparent';

  const borderColor = invalid ? theme.palette.error.main : panelBorder;

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: 1,
        overflow: 'hidden',
        mb: 4,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: expanded ? `1px solid ${panelBorder}` : 'none',
          bgcolor: headerBg,
          cursor: required || disabled ? 'default' : 'pointer',
        }}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('.MuiSwitch-root')) {
            return;
          }
          if (!required && !disabled && onEnabledChange) {
            onEnabledChange(!enabled);
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
          {!required && (
            expanded ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: inkSecondary }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: inkSecondary }} />
            )
          )}
          <Box minWidth={0}>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: invalid ? theme.palette.error.main : inkSecondary,
              }}
            >
              {title}
              {required && ' *'}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: inkSecondary, display: 'block' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {!required && onEnabledChange && (
          <Switch
            size="small"
            checked={enabled}
            onChange={(e) => {
              e.stopPropagation();
              onEnabledChange(e.target.checked);
            }}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: NAUTILUS.sand,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: alpha(NAUTILUS.sand, 0.5),
              },
            }}
          />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export default CollapsiblePanel;
